import React, { useState, useEffect, useMemo, useCallback } from 'react';

// LoadPanel
import { LoadPanel } from 'devextreme-react/load-panel';

// CheckBox
import CheckBox from 'devextreme-react/check-box';

// Grid
import DataGrid, {
  Column, FilterRow, HeaderFilter, FilterPanel, FilterBuilderPopup,
  GroupPanel, Grouping, SearchPanel, Pager, Paging, Scrolling,
  StateStoring, ColumnChooser, ColumnChooserSearch, ColumnChooserSelection, Export,
  Search,
} from 'devextreme-react/data-grid';

// Ruteador
import { Link } from 'react-router-dom';

// Iconos
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlasses } from '@fortawesome/pro-solid-svg-icons';
import { faPencilAlt } from '@fortawesome/pro-solid-svg-icons';
import { faList } from '@fortawesome/pro-duotone-svg-icons';

import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';

import { callProcesa } from '../api/callProcesa';

import './Locations.css';
import '../ListStyle.css';

export default function LocationsGrid(props) {
  const permissionLevel = props.nivel;

  const [locationRecords, setLocationRecords] = useState([]);
  const [autoExpandAll, setAutoExpandAll] = useState(true);
  const [visibleLoadPanel, setVisibleLoadPanel] = useState(false);

  function getOrderDay(rowData) {
    return new Date(rowData.OrderDate).getDay();
  }

  const filterBuilder = useMemo(() => {
    return {
      customOperations: [{
        name: 'weekends',
        caption: 'Weekends',
        dataTypes: ['date'],
        icon: 'check',
        hasValue: false,
        calculateFilterExpression: () => [[getOrderDay, '=', 0], 'or', [getOrderDay, '=', 6]],
      }],
      allowHierarchicalFields: true,
    };
  }, []);

  const filterBuilderPopupPosition = useMemo(() => {
    return { of: window, at: 'top', my: 'top', offset: { y: 10 } };
  }, []);

  const allowedPageSizes = useMemo(() => {
    return [5, 10, 15, 20, 'all'];
  }, []);

  const getLocationsList = useCallback(async () => {
    const datos = await callProcesa('/procesalocation', { action: 'list' });
    setLocationRecords(datos.data);
  }, []);

  useEffect(() => {
    async function initialize() {
      setVisibleLoadPanel(true);
      await getLocationsList();
      setVisibleLoadPanel(false);
    }
    initialize();
  }, [getLocationsList]);

  const onAutoExpandAllChanged = useCallback(() => {
    setAutoExpandAll((prev) => !prev);
  }, []);

  const editLocation = useCallback((data) => {
    return (
      <Link to={`/Locations/${data.data.internalid}`}>
        <button className="bg-blue-500 hover:bg-blue-700 text-white py-1 px-3 rounded"><FontAwesomeIcon icon={permissionLevel === 'Editar' ? faPencilAlt : faGlasses} /> {permissionLevel === 'Editar' ? 'Editar' : 'Ver'}</button>
      </Link>
    );
  }, [permissionLevel]);

  function onExporting(e) {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Datos');

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Datos.xlsx');
      });
    });
    e.cancel = true;
  }

  return (
    <div>
      <div className="bg-gray-200 m-2">
        <h1 className="font-sans inline-block text-xl font-bold text-gray-700 leading-8 align-top mb-1"><FontAwesomeIcon icon={faList} size="lg" /> Locations</h1>
      </div>

      <LoadPanel
        shadingColor="rgba(0,0,0,0.4)"
        visible={visibleLoadPanel}
        showIndicator={true}
        message="Por favor espere..."
        shading={true}
        showPane={true}
        hideOnOutsideClick={false}
      />

      <div className="bg-gray-200 m-2">
        <div className="bg-white p-3 shadow-xl">

          {permissionLevel === 'Editar' ? (
            <div>
              <Link to="/Locations/undefined">
                <button className="mb-2 bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"><FontAwesomeIcon icon={faList} /> Nueva Ubicación</button>
              </Link>
            </div>
          ) : (
            <div></div>
          )}

          <div className="options mb-2">
            <div className="option">
              <CheckBox text="Expandir todos los grupos"
                value={autoExpandAll}
                onValueChanged={onAutoExpandAllChanged}>
              </CheckBox>
            </div>
          </div>

          <DataGrid
            id="gridLocationContainer"
            columnsAutoWidth={true}
            filterBuilder={filterBuilder}
            dataSource={locationRecords}
            allowColumnReordering={true}
            allowColumnResizing={true}
            columnResizingMode="widget"
            showBorders={true}
            rowAlternationEnabled={true}
            showRowLines={true}
            showColumnLines={true}
            onExporting={onExporting}
          >
            <Scrolling rowRenderingMode='virtual'></Scrolling>
            <Paging defaultPageSize={10} />
            <Pager
              visible={true}
              allowedPageSizes={allowedPageSizes}
              displayMode='compact'
              showPageSizeSelector={true}
              showInfo={true}
              showNavigationButtons={true} />
            <FilterRow visible={true} />
            <FilterPanel visible={true} />
            <FilterBuilderPopup position={filterBuilderPopupPosition} />

            <HeaderFilter visible={true} />

            <GroupPanel visible={true} />
            <SearchPanel visible={true} />
            <ColumnChooser
              enabled={true}
              mode='select'
            >
              <ColumnChooserSearch
                enabled={true}
              />

              <ColumnChooserSelection
                allowSelectAll={true}
                selectByClick={true}
                recursive={true}
              />
            </ColumnChooser>

            <Grouping autoExpandAll={autoExpandAll} />
            <Export enabled={true} />
            <StateStoring enabled={true} type="localStorage" storageKey="gridContainerListLocations" />

            <Column caption="" alignment="center" width={100} cellRender={editLocation} />

            <Column dataField="parent" caption="Ubicación Padre" dataType="string" width={250} groupIndex={0} >
              <HeaderFilter>
                <Search enabled={true} />
              </HeaderFilter>
            </Column>
            <Column dataField="name" caption="Ubicación" dataType="string" width={250} >
              <HeaderFilter>
                <Search enabled={true} />
              </HeaderFilter>
            </Column>
            <Column dataField="subsidiary" caption="Subsidiaria" dataType="string" width={250} >
              <HeaderFilter>
                <Search enabled={true} />
              </HeaderFilter>
            </Column>
            <Column dataField="custrecord_vm_tipo_ubicacion" caption="Tipo de Ubicación" dataType="string" width={250} >
              <HeaderFilter>
                <Search enabled={true} />
              </HeaderFilter>
            </Column>
            <Column dataField="city" caption="Ciudad" dataType="string" width={200} visible={false} >
              <HeaderFilter>
                <Search enabled={true} />
              </HeaderFilter>
            </Column>
            <Column dataField="internalid" caption="internalid" dataType="number" width={200} visible={false} >
              <HeaderFilter>
                <Search enabled={true} />
              </HeaderFilter>
            </Column>
            <Column dataField="custrecordcustrecord_vm_nombrecorto" caption="Nombre Corto" dataType="string" width={200} visible={true} >
              <HeaderFilter>
                <Search enabled={true} />
              </HeaderFilter>
            </Column>
            <Column dataField="custrecord_gv_rlx_idrelex" caption="ID Ubicación Relex" dataType="string" width={200} visible={true} >
              <HeaderFilter>
                <Search enabled={true} />
              </HeaderFilter>
            </Column>
            <Column dataField="custrecord_al_rlx_safety_lead_time" caption="Relex Safety Lead Time" dataType="number" width={200} visible={false} >
              <HeaderFilter>
                <Search enabled={true} />
              </HeaderFilter>
            </Column>
            <Column dataField="custrecord_al_rlx_closing_date" caption="Relex Closing Date" dataType="date" width={200} visible={false} >
              <HeaderFilter>
                <Search enabled={true} />
              </HeaderFilter>
            </Column>
            <Column dataField="custrecord_al_rlx_opening_date" caption="Relex Opening Date" dataType="date" width={200} visible={false} >
              <HeaderFilter>
                <Search enabled={true} />
              </HeaderFilter>
            </Column>
            <Column dataField="custrecord_al_rlx_location_type" caption="Relex Location Type" dataType="string" width={200} visible={false} >
              <HeaderFilter>
                <Search enabled={true} />
              </HeaderFilter>
            </Column>
            <Column dataField="custrecord_gv_rlx_tmklocation" caption="Telemarketing Location" dataType="string" width={200} visible={false} >
              <HeaderFilter>
                <Search enabled={true} />
              </HeaderFilter>
            </Column>
            <Column dataField="custrecord_gv_rlx_cdclocation" caption="Consumption Center Location" dataType="string" width={200} visible={false} >
              <HeaderFilter>
                <Search enabled={true} />
              </HeaderFilter>
            </Column>
            <Column dataField="custrecord_gv_rlx_receiptlocation" caption="Receipt Location" dataType="string" width={200} visible={false} >
              <HeaderFilter>
                <Search enabled={true} />
              </HeaderFilter>
            </Column>
            <Column dataField="custrecord_gv_rlx_tmklocation_id" caption="Telemarketing Location ID" dataType="string" width={200} visible={false} >
              <HeaderFilter>
                <Search enabled={true} />
              </HeaderFilter>
            </Column>
            <Column dataField="custrecord_gv_rlx_cdclocation_id" caption="Consumption Center Location ID" dataType="string" width={200} visible={false} >
              <HeaderFilter>
                <Search enabled={true} />
              </HeaderFilter>
            </Column>
            <Column dataField="custrecord_gv_rlx_receiptlocation_id" caption="Receipt Location ID" dataType="string" width={200} visible={false} >
              <HeaderFilter>
                <Search enabled={true} />
              </HeaderFilter>
            </Column>
          </DataGrid>
        </div>
      </div>
    </div>
  );
}
